import { useState, useEffect, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import type { Family } from '@/context/AuthContext'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'
import EmailPreferencesSection from '@/components/EmailPreferencesSection'
import ActReminderSection from '@/components/ActReminderSection'
import MagnumSays from '@/components/MagnumSays'
import TvManagementPage from '@/pages/TvManagementPage'

function formatAge(birthDate: number | null): string {
  if (!birthDate) return 'Age not set'
  const ageDiff = Date.now() - birthDate
  const years = Math.floor(ageDiff / (365.25 * 24 * 60 * 60 * 1000))
  return `${years} years old`
}

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Phoenix',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
]

function tzLabel(tz: string) {
  return tz.replace(/_/g, ' ').replace(/\//g, ' / ')
}

const TABS = [
  { key: 'account', label: 'Account' },
  { key: 'children', label: 'Children' },
  { key: 'family', label: 'Adults' },
  { key: 'tvs', label: 'TVs' },
  { key: 'notifications', label: 'Notifications' },
] as const

type TabKey = typeof TABS[number]['key']

function AccountTab({ parent, timezone, onTimezoneChange, savingTz }: {
  parent: any
  timezone: string
  onTimezoneChange: (tz: string) => void
  savingTz: boolean
}) {
  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-black text-foreground">Your Account</h2>
        <p className="mt-1 text-muted-foreground">Manage your profile and preferences</p>
      </div>

      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <p className="font-medium">{parent.firstName} {parent.lastName}</p>
          <p className="text-sm text-muted-foreground">{parent.emailAddress}</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Timezone</label>
          <p className="text-xs text-muted-foreground mb-2">
            Daily incentive goals reset at midnight in this timezone.
          </p>
          <select
            value={timezone}
            onChange={(e) => onTimezoneChange(e.target.value)}
            disabled={savingTz}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tzLabel(tz)}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

function ChildrenTab({ children: existingChildren, onRefresh }: {
  children: any[]
  onRefresh: () => void
}) {
  const [showForm, setShowForm] = useState(false)
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await apiFetch('/children', {
        method: 'POST',
        body: JSON.stringify({
          username,
          displayName,
          password,
          birthDate: birthDate ? new Date(birthDate).getTime() : null,
        }),
      })
      setUsername('')
      setDisplayName('')
      setPassword('')
      setBirthDate('')
      setShowForm(false)
      await onRefresh()
    } catch (err: any) {
      setError(err.message || 'Failed to add child.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-black text-foreground">Your Readers</h2>
        {existingChildren.length > 0 && (
          <p className="mt-1 text-muted-foreground">Manage your children's accounts</p>
        )}
      </div>

      {existingChildren.length === 0 ? (
        <MagnumSays>
          <p className="font-medium">Let's add your first reader!</p>
          <p className="mt-1 text-muted-foreground">
            They'll get their own login so they can practice independently.
          </p>
        </MagnumSays>
      ) : (
        <div className="space-y-2">
          {existingChildren.map((child: any) => (
            <ChildCard key={child.id} child={child} onRefresh={onRefresh} />
          ))}
        </div>
      )}

      {showForm ? (
        <form onSubmit={handleAdd} className="space-y-3">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}

          <div>
            <label htmlFor="s-childName" className="block text-sm font-medium mb-1">Display Name</label>
            <input
              id="s-childName"
              type="text"
              required
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="How they'll appear in the app"
            />
          </div>

          <div>
            <label htmlFor="s-childUser" className="block text-sm font-medium mb-1">Username</label>
            <input
              id="s-childUser"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="For logging in"
            />
          </div>

          <div>
            <label htmlFor="s-childPass" className="block text-sm font-medium mb-1">Password</label>
            <input
              id="s-childPass"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="At least 4 characters"
            />
          </div>

          <div>
            <label htmlFor="s-childBirth" className="block text-sm font-medium mb-1">
              Birth Date
            </label>
            <input
              id="s-childBirth"
              type="date"
              required
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="flex-1 py-2.5 border border-border rounded-lg font-medium hover:bg-muted transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Child'}
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full py-2.5 border border-dashed border-border rounded-lg text-muted-foreground font-medium hover:border-primary hover:text-primary transition min-h-[44px]"
        >
          + Add another child
        </button>
      )}
    </div>
  )
}

function ChildCard({ child, onRefresh }: { child: any; onRefresh: () => void }) {
  const [showPassword, setShowPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [editingEmail, setEditingEmail] = useState(false)
  const [email, setEmail] = useState(child.emailAddress || '')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await apiFetch(`/children/${child.id}/password`, {
        method: 'PUT',
        body: JSON.stringify({ password: newPassword }),
      })
      setNewPassword('')
      setSuccess('password')
      setTimeout(() => { setSuccess(''); setShowPassword(false) }, 2000)
    } catch (err: any) {
      setError(err.message || 'Failed to change password.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveEmail = async () => {
    setError('')
    setSubmitting(true)
    try {
      await apiFetch(`/children/${child.id}`, {
        method: 'PUT',
        body: JSON.stringify({ emailAddress: email.trim() || null }),
      })
      setEditingEmail(false)
      setSuccess('email')
      setTimeout(() => setSuccess(''), 2000)
      onRefresh()
    } catch (err: any) {
      setError(err.message || 'Failed to update email.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    setError('')
    setSubmitting(true)
    try {
      await apiFetch(`/children/${child.id}`, { method: 'DELETE' })
      onRefresh()
    } catch (err: any) {
      setError(err.message || 'Failed to remove child.')
    } finally {
      setSubmitting(false)
      setConfirmDelete(false)
    }
  }

  return (
    <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
          {child.displayName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{child.displayName}</p>
          <p className="text-xs text-muted-foreground">@{child.username} &middot; {formatAge(child.birthDate)}</p>
        </div>
      </div>

      {/* Email */}
      <div className="pl-11">
        {editingEmail ? (
          <div className="flex gap-2">
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="flex-1 px-2 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={handleSaveEmail}
              disabled={submitting}
              className="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
            >
              {submitting ? '...' : 'Save'}
            </button>
            <button
              onClick={() => { setEditingEmail(false); setEmail(child.emailAddress || '') }}
              className="text-sm px-2 py-1.5 text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditingEmail(true)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {child.emailAddress ? child.emailAddress : '+ Add email'}
            {success === 'email' && <span className="text-primary ml-2">Saved!</span>}
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="pl-11 flex flex-wrap gap-2">
        {showPassword ? (
          success === 'password' ? (
            <p className="text-sm text-primary font-medium">Password updated!</p>
          ) : (
            <form onSubmit={handleChangePassword} className="flex gap-2 w-full">
              <input
                type="password"
                required
                minLength={4}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                className="flex-1 px-2 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                disabled={submitting}
                className="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
              >
                {submitting ? '...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowPassword(false)}
                className="text-sm px-2 py-1.5 text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </form>
          )
        ) : confirmDelete ? (
          <div className="space-y-3 w-full">
            <MagnumSays variant="warning" size="sm">
              <p className="font-medium">Whoa, hold on!</p>
              <p className="mt-1 text-muted-foreground">
                Deleting {child.displayName}'s account will erase all their practice history, statistics, and progress forever. I can't undo this one!
              </p>
            </MagnumSays>
            <div className="flex items-center gap-2 pl-11">
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50"
              >
                {submitting ? '...' : 'Yes, permanently delete'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-xs px-2 py-1.5 text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <Link
              to={`/dashboard/children/${child.id}/game-settings`}
              className="text-xs text-primary hover:underline"
            >
              Game Settings
            </Link>
            <span className="text-xs text-muted-foreground">&middot;</span>
            <Link
              to={`/dashboard/children/${child.id}/goals`}
              className="text-xs text-primary hover:underline"
            >
              Goals
            </Link>
            <span className="text-xs text-muted-foreground">&middot;</span>
            <Link
              to={`/dashboard/children/${child.id}/screen-time`}
              className="text-xs text-primary hover:underline"
            >
              Screen Time
            </Link>
            <span className="text-xs text-muted-foreground">&middot;</span>
            <button
              onClick={() => setShowPassword(true)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Change Password
            </button>
            <span className="text-xs text-muted-foreground">&middot;</span>
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </>
        )}
      </div>

      {error && <p className="text-xs text-red-600 pl-11">{error}</p>}
    </div>
  )
}

function FamilyTab({ families, parentId, onRefresh }: {
  families: Family[]
  parentId: number
  onRefresh: () => void
}) {
  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-black text-foreground">Adults</h2>
        <p className="mt-1 text-muted-foreground">Manage who has access to your family</p>
      </div>

      {families.length > 0 ? (
        <div className="space-y-4">
          {families.map((family) => (
            <FamilyCard key={family.id} family={family} parentId={parentId} onRefresh={onRefresh} />
          ))}
        </div>
      ) : (
        <p className="text-center text-muted-foreground text-sm py-4">No families found.</p>
      )}
    </div>
  )
}

function FamilyCard({ family, parentId, onRefresh }: { family: Family; parentId: number; onRefresh: () => void }) {
  const [showInvite, setShowInvite] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [familyName, setFamilyName] = useState(family.name)
  const [savingSignoff, setSavingSignoff] = useState(false)
  const isAdmin = family.role === 'ADMIN'

  const handleNameSave = async () => {
    if (familyName.trim() === family.name) {
      setEditingName(false)
      return
    }
    try {
      await apiFetch(`/families/${family.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: familyName.trim() }),
      })
      setEditingName(false)
      onRefresh()
    } catch {
      setFamilyName(family.name)
      setEditingName(false)
    }
  }

  const handleToggleSignoff = async () => {
    setSavingSignoff(true)
    try {
      await apiFetch(`/families/${family.id}`, {
        method: 'PUT',
        body: JSON.stringify({ requireSignoff: !family.requireSignoff }),
      })
      onRefresh()
    } catch { /* ignore */ }
    finally { setSavingSignoff(false) }
  }

  const handleRemoveMember = async (memberId: number) => {
    try {
      await apiFetch(`/families/${family.id}/members/${memberId}`, { method: 'DELETE' })
      onRefresh()
    } catch { /* ignore */ }
  }

  const handleRevokeInvite = async (inviteId: number) => {
    try {
      await apiFetch(`/families/${family.id}/invites/${inviteId}`, { method: 'DELETE' })
      onRefresh()
    } catch { /* ignore */ }
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-center justify-between bg-primary/5">
        {editingName && isAdmin ? (
          <input
            autoFocus
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
            className="text-lg font-semibold bg-transparent border-b border-primary focus:outline-none"
          />
        ) : (
          <h3
            className={`text-lg font-semibold ${isAdmin ? 'cursor-pointer hover:text-primary' : ''}`}
            onClick={() => isAdmin && setEditingName(true)}
          >
            {family.name}
          </h3>
        )}
        <span className="text-xs px-2 py-1 rounded-full bg-background border border-border text-muted-foreground">
          {family.role === 'ADMIN' ? 'Admin' : 'Member'}
        </span>
      </div>

      {/* Children */}
      <div className="p-4 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Children</p>
        {family.children.length === 0 ? (
          <p className="text-sm text-muted-foreground">No children in this family yet.</p>
        ) : (
          <div className="space-y-1.5">
            {family.children.map(({ child, kidRole }) => (
              <div key={child.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                    {child.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{child.displayName}</p>
                    <p className="text-xs text-muted-foreground">@{child.username}</p>
                  </div>
                </div>
                {kidRole === 'KID_ADMIN' && (
                  <div className="flex items-center gap-1.5">
                    <Link
                      to={`/dashboard/children/${child.id}/goals`}
                      className="text-xs px-2 py-1 border border-border rounded-md hover:bg-accent transition min-h-[36px] inline-flex items-center"
                    >
                      Goals
                    </Link>
                    <Link
                      to={`/dashboard?child=${child.id}`}
                      className="text-xs px-2 py-1 border border-border rounded-md hover:bg-accent transition min-h-[36px] inline-flex items-center"
                    >
                      Dashboard
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members */}
      {isAdmin && (
        <div className="p-4 border-t border-border space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Adults</p>
          <div className="space-y-2">
            {family.members.map((member) => (
              <div key={member.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-bold text-xs">
                    {(member.firstName || member.email).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <span className="font-medium">
                      {member.firstName || member.email}
                      {member.lastName ? ` ${member.lastName}` : ''}
                    </span>
                    {member.parentId !== parentId && member.firstName && (
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-1.5 py-0.5 rounded bg-accent text-muted-foreground">
                    {member.role === 'ADMIN' ? 'Admin' : 'Member'}
                  </span>
                  {member.parentId !== parentId && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pending invites */}
          {family.pendingInvites.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Pending</p>
              {family.pendingInvites.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{invite.email}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {invite.familyRole === 'ADMIN' ? 'Co-Parent' : 'Custom'}
                    </span>
                    <button
                      onClick={() => handleRevokeInvite(invite.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Invite form */}
          {showInvite ? (
            <InviteMemberForm
              familyId={family.id}
              onDone={() => { setShowInvite(false); onRefresh() }}
            />
          ) : (
            <button
              onClick={() => setShowInvite(true)}
              className="w-full py-2.5 border border-dashed border-border rounded-lg text-muted-foreground font-medium hover:border-primary hover:text-primary transition min-h-[44px]"
            >
              + Invite a co-parent or adult
            </button>
          )}
        </div>
      )}

      {/* Screen time rules */}
      {isAdmin && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Require parent signoff before allowance</p>
              <p className="text-xs text-muted-foreground">
                Kids must get a parent's approval each day (like a homework check) before
                redeeming screen time
              </p>
            </div>
            <button
              onClick={handleToggleSignoff}
              disabled={savingSignoff}
              className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                family.requireSignoff ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                family.requireSignoff ? 'left-[18px]' : 'left-0.5'
              }`} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function InviteMemberForm({ familyId, onDone }: { familyId: number; onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [familyRole, setFamilyRole] = useState('ADMIN')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await apiFetch(`/families/${familyId}/invites`, {
        method: 'POST',
        body: JSON.stringify({ email, familyRole }),
      })
      setEmail('')
      setFamilyRole('ADMIN')
      onDone()
    } catch (err: any) {
      setError(err.message || 'Failed to send invite.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div>
        <label htmlFor="s-inviteEmail" className="block text-sm font-medium mb-1">Email Address</label>
        <input
          id="s-inviteEmail"
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="partner@example.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Access Level</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFamilyRole('ADMIN')}
            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
              familyRole === 'ADMIN' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
            }`}
          >
            Co-Parent
          </button>
          <button
            type="button"
            onClick={() => setFamilyRole('MEMBER')}
            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
              familyRole === 'MEMBER' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
            }`}
          >
            Custom
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {familyRole === 'ADMIN' ? 'Full access to manage children, goals, and settings' : 'View-only access, customizable per child'}
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onDone}
          className="flex-1 py-2.5 border border-border rounded-lg font-medium hover:bg-muted transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
        >
          {submitting ? 'Sending...' : 'Send Invite'}
        </button>
      </div>
    </form>
  )
}

function NotificationsTab({ children }: { children: any[] }) {
  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-black text-foreground">Notifications</h2>
        <p className="mt-1 text-muted-foreground">Choose what emails you receive</p>
      </div>

      {children.length > 0 ? (
        <>
          <EmailPreferencesSection children={children.map((c: any) => ({ id: c.id, displayName: c.displayName }))} />
          <ActReminderSection children={children.map((c: any) => ({ id: c.id, displayName: c.displayName }))} />
        </>
      ) : (
        <MagnumSays>
          Add a reader first, then I'll let you set up email notifications for their progress!
        </MagnumSays>
      )}
    </div>
  )
}

export default function SettingsPage() {
  const { user, refresh } = useAuth()
  const [activeTab, setActiveTab] = useState<TabKey>('account')
  const [timezone, setTimezone] = useState('')
  const [savingTz, setSavingTz] = useState(false)

  useEffect(() => {
    if (user?.type === 'parent' && user.parent?.timezone) {
      setTimezone(user.parent.timezone)
    }
  }, [user])

  if (!user || user.type !== 'parent') return null

  const { parent, children, families } = user

  const handleTimezoneChange = async (newTz: string) => {
    setTimezone(newTz)
    setSavingTz(true)
    try {
      await apiFetch('/app/settings', {
        method: 'PUT',
        body: JSON.stringify({ timezone: newTz }),
      })
      await refresh()
    } catch {
      // ignore
    } finally {
      setSavingTz(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-center mb-6">Settings</h1>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-lg border border-border bg-muted p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition min-h-[44px]',
                activeTab === tab.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === 'account' && (
          <AccountTab
            parent={parent}
            timezone={timezone}
            onTimezoneChange={handleTimezoneChange}
            savingTz={savingTz}
          />
        )}
        {activeTab === 'children' && (
          <ChildrenTab children={children} onRefresh={refresh} />
        )}
        {activeTab === 'family' && (
          <FamilyTab families={families || []} parentId={parent.id} onRefresh={refresh} />
        )}
        {activeTab === 'tvs' && (
          <TvManagementPage />
        )}
        {activeTab === 'notifications' && (
          <NotificationsTab children={children} />
        )}
      </div>
    </div>
  )
}
